'use client';

import Image from 'next/image';
import { Fragment, useEffect, useMemo, useState } from 'react';

import Spinner from '@/components/atoms/Spinner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { Comedian_Entry } from '@/types/custom';
import { getComedianPageLink } from '@/utils/comedian';
import { useTranslations } from 'next-intl';

interface OpenArtistPanelEvent extends CustomEvent {
    detail: {
        slug: string;
        url: string;
    };
}

declare global {
    interface WindowEventMap {
        openArtistPanel: OpenArtistPanelEvent;
    }
}

export default function ArtistModal({ locale }: { locale: string }) {
    const t = useTranslations('ArtistModal');
    const [artist, setArtist] = useState<Comedian_Entry | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [currentSlug, setCurrentSlug] = useState<string>('');

    // Memoize the artist data
    const memoizedArtist = useMemo(() => artist, [artist]);

    // Listen for custom events to open artist panel
    useEffect(() => {
        const handleOpenArtistPanel = (event: OpenArtistPanelEvent) => {
            const { slug } = event.detail;

            if (slug) {
                setCurrentSlug(slug);
                setIsOpen(true);
            }
        };

        window.addEventListener('openArtistPanel', handleOpenArtistPanel);
        return () => {
            window.removeEventListener('openArtistPanel', handleOpenArtistPanel);
        };
    }, []);

    // Fetch artist data when modal is opened with a slug
    useEffect(() => {
        if (!isOpen || !currentSlug) {
            return;
        }

        setIsLoading(true);

        fetch(`/api/${locale}/artists/${currentSlug}`)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`Failed to fetch artist with status: ${res.status}`);
                }
                return res.json();
            })
            .then((data) => {
                if (data.error) {
                    throw new Error(data.error);
                }

                // Check if we got valid artist data
                if (!data || !data.id) {
                    throw new Error('Artist not found');
                }

                setArtist(data);
            })
            .catch((err) => {
                console.error('Error fetching artist:', err);
                setIsOpen(false);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [locale, currentSlug, isOpen]);

    // Don't render the modal at all if it's not open
    if (!isOpen) {
        return null;
    }

    const largeImage = memoizedArtist?.largeImages?.[0] || null;

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent className="h-screen w-screen border-none p-0 shadow-lg sm:h-[80vh] sm:w-[80vw] sm:max-w-screen-lg [&>button]:absolute [&>button]:top-4 [&>button]:right-4 [&>button]:z-10 [&>button]:flex [&>button]:h-8 [&>button]:w-8 [&>button]:items-center [&>button]:justify-center [&>button]:bg-haug-ivory-100 [&>button]:text-haug-choco-100">
                <DialogHeader className="sr-only">
                    <DialogTitle>{memoizedArtist?.title || 'Artist'}</DialogTitle>
                </DialogHeader>

                {isLoading ? (
                    <div className="flex h-full items-center justify-center rounded-lg bg-black/20">
                        <Spinner variant="white" size="md" />
                    </div>
                ) : (
                    memoizedArtist && (
                        <div className="artist-modal h-full overflow-y-auto font-neue-haas sm:flex">
                            <div className="flex w-full items-center sm:w-1/2">
                                <div className="relative block h-full w-full">
                                    {largeImage?.url && (
                                        <Image
                                            src={largeImage.url}
                                            width={largeImage.width}
                                            height={largeImage.height}
                                            alt={memoizedArtist.title || ''}
                                            sizes="100vw"
                                            priority={false}
                                            loading="lazy"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                            }}
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col bg-haug-ivory-100 sm:w-1/2">
                                <div className="p-4 sm:p-8">
                                    <div className={'h1 pb-6'}>{memoizedArtist.title}</div>

                                    <div className="pb-8">
                                        {memoizedArtist.artistShortDescription
                                            ?.split('\n')
                                            .map((item, idx) => (
                                                <Fragment key={`${memoizedArtist.id}-desc-${idx}`}>
                                                    {item}
                                                    <br />
                                                </Fragment>
                                            ))}
                                    </div>

                                    {artist && memoizedArtist.clubHaugRegular && (
                                        <a
                                            href={getComedianPageLink(artist)}
                                            className="button block w-full bg-haug-lila-100 text-center text-haug-ivory-100 hover:bg-haug-lila-100/80 hover:bg-haug-lila-200"
                                        >
                                            {t('read-more')}
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    )
                )}
            </DialogContent>
        </Dialog>
    );
}
